<template>
	<div class="content-area__content">
		<vue-title :title="titleText"></vue-title>
		<h1 class="section-title" data-test-element="changepassword-title">{{ titleText }}</h1>

		<Form v-slot="{ validate, setErrors, setFieldError }">
			<form ref="form" action="authentication/changepassword" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">
				<div class="validation-summary-errors alert alert-danger fade show"
						 role="alert"
						 data-valmsg-summary="true"
						 v-show="model.errorMessage && !hasAnyFieldErrors"
						 data-test-element="idp-alert-danger-generic">
					<i class="material-icons">warning</i>
					<span data-test-element="changepassword-errormessage">{{ model.errorMessage }}</span>
				</div>

				<div class="validation-summary-errors alert alert-danger fade show"
						 role="alert"
						 data-valmsg-summary="true"
						 v-show="hasAnyFieldErrors">
					<i class="material-icons">warning</i>
					<span v-if="formSummaryMessage">{{ formSummaryMessage }}</span>
					<span v-else class="alert-message" data-test-element="alert-message">{{ $t('validation:summary') }}</span>
				</div>

				<fieldset>
					<div class="panel-message" data-test-element="changepassword-instructionmessage">
						{{ instructionsText }}
					</div>

					<div class="form-group">
						<label>
							<span class="label" data-test-element="changepassword-label-oldpassword">
								{{ oldPasswordLabel }}
							</span>
							<span class="control-with-help-offset">
								<Field name="OldPassword" :label="oldPasswordLabel" :rules="{ required: true }" v-model="model.oldPassword" v-slot="{ field, meta }">
									<input type="password"
												 v-bind="field"
												 name="OldPassword"
												 class="control"
												 data-test-element="changepassword-oldpassword"
												 v-focus
												 autocomplete="off"
												 :class="{ 'input-validation-error': submitted && !meta.valid }" />
								</Field>
							</span>
						</label>
						<ErrorMessage v-if="submitted" name="OldPassword" v-slot="{ message }">
							<span class="field-validation-error" data-test-element="changepassword-oldpassword-validationerror">{{ message }}</span>
						</ErrorMessage>
					</div>

					<div class="form-group">
						<label v-on:click.stop>
							<div class="newpassword-label-row" data-test-element="changepassword-label-newpassword">
								<span class="label">{{ newPasswordLabel }}</span>
								<popper-help-component class="newpassword-help"
																			 :text="model.complexityHelp"
																			 data-test-element="changepassword-complexityhelp"
																			 tabindex="0">
								</popper-help-component>
							</div>

							<span class="control-with-help-offset">
								<Field name="NewPassword" :label="newPasswordLabel" :rules="newPasswordRules" v-model="model.newPassword" v-slot="{ field, meta }">
									<input type="password"
												 v-bind="field"
												 name="NewPassword"
												 id="newpassword"
												 class="control -with-help"
												 data-test-element="changepassword-newpassword"
												 @change="onNewPasswordChange"
												 autocomplete="off"
												 :class="{ 'input-validation-error': submitted && !meta.valid }" />
								</Field>
							</span>
						</label>
						<ErrorMessage v-if="submitted" name="NewPassword" v-slot="{ message }">
							<span class="field-validation-error" data-test-element="changepassword-newpassword-validationerror">{{ message }}</span>
						</ErrorMessage>
						<span class="field-validation-error"
									data-test-element="changepassword-newpassword-customvalidationerror"
									id="newpassword-validationerror"
									style="display:none;"></span>
					</div>

					<div class="form-group">
						<label>
							<span class="label" data-test-element="changepassword-label-confirmpassword">
								{{ confirmPasswordLabel }}
							</span>
							<span class="control-with-help-offset">
								<Field name="ConfirmPassword" :label="confirmPasswordLabel" :rules="confirmRules" v-model="model.confirmPassword" v-slot="{ field, meta }">
									<input type="password"
												 v-bind="field"
												 name="ConfirmPassword"
												 class="control"
												 data-test-element="changepassword-confirmpassword"
												 autocomplete="off"
												 :class="{ 'input-validation-error': submitted && !meta.valid }" />
								</Field>
							</span>
						</label>
						<ErrorMessage v-if="submitted" name="ConfirmPassword" v-slot="{ message }">
							<span class="field-validation-error" data-test-element="changepassword-confrimpassword-validationerror">{{ message }}</span>
						</ErrorMessage>
					</div>

					<div class="form-actions">
						<button type="submit" name="Action:ChangePassword" class="button" data-test-element="changepassword-continuebutton" :disabled="disabled">{{ $t('shared:action:continue') }}</button>
						<a @click.prevent="loginCancel" href="authentication/logincancel" data-test-element="changepassword-cancellink" class="button-secondary link-text-transform">{{ $t('shared:action:cancel') }}</a>
					</div>
				</fieldset>
			</form>
		</Form>
	</div>
</template>

<script>
	import redirectHandler from 'utils/redirect';
	import PopperHelpVue from 'components/popper-help';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import validation from 'utils/validation-mixin';
	import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';
	import { fmt } from 'utils/i18n-format';

	export default {
		name: 'ChangePassword',
		components: {
			'popper-help-component': PopperHelpVue,
			Form,
			Field,
			ErrorMessage
		},
		mixins: [validation],

		data: () => ({
			model: {
				oldPassword: null,
				newPassword: null,
				confirmPassword: null,
				complexityHelp: null
			},
			disabled: false,
			submitted: false,
			hasAnyFieldErrors: false,
			formSummaryMessage: null
		}),

		computed: {
			passwordLabel() {
				return this.$t('shared:fields:password');
			},
			titleText() {
				return fmt(this.$t('identityProvider:changePassword:title'), this.passwordLabel);
			},
			instructionsText() {
				return fmt(this.$t('identityProvider:changePassword:instructions'), String(this.passwordLabel).toLowerCase());
			},
			oldPasswordLabel() {
				return fmt(this.$t('identityProvider:changePassword:oldPassword'), this.passwordLabel);
			},
			newPasswordLabel() {
				return fmt(this.$t('identityProvider:changePassword:newPassword'), this.passwordLabel);
			},
			confirmPasswordLabel() {
				return fmt(this.$t('identityProvider:changePassword:confirmPassword'), this.passwordLabel);
			},

			newPasswordRules() {
				const rules = { required: true };
				return rules;
			},
			confirmRules() {
        return { required: true, confirmed: ['@NewPassword', this.newPasswordLabel] };
			}
		},

		methods: {
			onNewPasswordChange() {
				$('#newpassword-validationerror').text('').css('display', 'none');
				$('#newpassword').removeClass('input-validation-error');
			},

			isOldAndNewPasswordAreSame() {
				return this.model.oldPassword === this.model.newPassword;
			},

			async validateBeforeSubmit(validate, setErrors, setFieldError) {
				$('#newpassword-validationerror').text('').css('display', 'none');
				this.formSummaryMessage = null;
				this.model.errorMessage = null;
				this.hasAnyFieldErrors = false;
				this.submitted = true;

				const result = await validate();
				const isValid = result === true || result?.valid === true;
				if (!isValid) {
					this.hasAnyFieldErrors = true;
					focusFirstInvalid(this.$refs.form);
					return;
				}

				if (this.isOldAndNewPasswordAreSame()) {
					const errorMsg = fmt(
						this.$t('identityProvider:changePassword:complexity:history'),
						null,
						null,
						String(this.passwordLabel).toLowerCase()
					);
					$('#newpassword-validationerror').text(errorMsg).css('display', 'block');
					$('#newpassword').addClass('input-validation-error');
					this.hasAnyFieldErrors = true;
					const el = document.getElementById('newpassword');
					if (el && typeof el.focus === 'function') el.focus();
					return;
				}

				await this.post(setErrors, setFieldError);
			},

			async post(setErrors, setFieldError) {
				const form = this.$refs.form;
				this.disabled = true;
				try {
					let response = await this.$http.post(form.action, this.model);

          // reset UI before redirect to avoid validation flicker
          this.submitted = false;
          this.hasAnyFieldErrors = false;
          this.hasError = false;
          this.formSummaryMessage = null;
          this.model.errorMessage = null;

          if (typeof setErrors === 'function') {
            setErrors({});
          }
          if (typeof setFieldError === 'function') {
            setFieldError('OldPassword', undefined);
            setFieldError('NewPassword', undefined);
            setFieldError('ConfirmPassword', undefined);
          }

					this.model = response.data;
					redirectHandler.checkAndRedirect(response, this.$router);
				} catch (error) {
          const resp = error?.response;

          // Primary path for Vue 3 + vee-validate v4
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            const bag = resp?.data?.errors;
            const s = bag && (bag[''] || bag['.']);
            const m = Array.isArray(s) ? s.find(Boolean) : s;
            this.formSummaryMessage = m || null;
            this.hasAnyFieldErrors = true;
            focusFirstInvalid(form);
            return;
          }

          // Legacy fallback only (guarded)
          try {
            if (this.validateResponse?.(form, resp)) {
              this.hasAnyFieldErrors = true;
              focusFirstInvalid(form);
              return;
            }
          } catch {
            // ignore legacy-path failures
          }

          throw error;
				} finally {
					this.disabled = false;
				}
			},

			async loginCancel(event) {
				let response = await this.$http.post(event.target.href);
				redirectHandler.checkAndRedirect(response, this.$router);
			}
		},

		async mounted() {
			let form = this.$refs.form;
			let response = await this.$http.get(form.action);
			this.model = response.data;
		}
	};
</script>